import React from "react";
import { Link, useNavigate } from "react-router";
import { CircleQuestionMark } from "lucide-react";

import Modal from "../../../shared/ui/Modal";
import { useAuth } from "../../auth/AuthContext";
import { canAccessItemImport } from "../itemImportAccess";
import { checkSelectedImportGids } from "../itemImportGidBatch";
import { importCodeBorderClass, lookupErrorMessage } from "../itemImportGidStatus";
import { hasImportRegistrationError, importRowErrorMessage, importRowStatus } from "../itemImportRowStatus";
import {
  canCheckImportSelection,
  canRegisterImportRow,
  canRegisterImportSelection,
  canSelectImportRow,
  selectImportRows,
  selectedImportRows,
  type ImportSelectionCondition,
} from "../itemImportSelection";
import {
  checkImportDuplicate,
  createImportRow,
  lookupImportGid,
  previewItems,
  type ImportPreview,
  type ImportRow,
} from "../itemImport.api";

function inferCompareSite(value: string): { site: string; fallback: boolean } {
  try {
    const host = new URL(value).hostname.toLowerCase();
    if (host === "shopping.naver.com" || host.endsWith(".shopping.naver.com")) {
      return { site: "naver", fallback: false };
    }
    if (host === "danawa.com" || host.endsWith(".danawa.com")) {
      return { site: "danawa", fallback: false };
    }
    if (host === "enuri.com" || host.endsWith(".enuri.com")) {
      return { site: "enuri", fallback: false };
    }
  } catch {
    // 형식 오류는 행에 따로 표시한다.
  }
  return { site: "naver", fallback: true };
}

function canTryAssignMyCompareItem(site: string, url: string): boolean {
  return site === "danawa" || site === "enuri" || (
    site === "naver" && /^https:\/\/search\.shopping\.naver\.com\/catalog\/\d+\/?(?:[?#]|$)/.test(url)
  );
}

function validTargetRank(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 3;
}

function recalculate(rows: ImportRow[], preview: ImportPreview): ImportRow[] {
  const completedCount = rows.filter((row) => row.completed).length;
  const remaining = preview.summary.max_item_count
    ? Math.max(0, preview.summary.max_item_count - preview.summary.registered_item_count - completedCount)
    : null;
  const seen = new Set<string>();
  let candidates = 0;
  return rows.map((row) => {
    const key = JSON.stringify([row.expected_market_site, row.shop_item_id, row.compare_url]);
    const duplicate = !row.completed && (row.db_duplicate || seen.has(key));
    if (row.expected_market_site && row.shop_item_id && row.compare_url) seen.add(key);
    if (!row.completed && !duplicate && Object.keys(row.errors).length === 0) candidates += 1;
    const over_limit = !row.completed && !duplicate && Object.keys(row.errors).length === 0
      && remaining !== null && candidates > remaining;
    return { ...row, duplicate, over_limit, selected: row.selected && !duplicate && !over_limit && !row.completed };
  });
}

export default function ItemImportPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<ImportPreview | null>(null);
  const [rows, setRows] = React.useState<ImportRow[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [checkingCodes, setCheckingCodes] = React.useState(false);
  const [showGapPriceBasisHelp, setShowGapPriceBasisHelp] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [progress, setProgress] = React.useState("");

  React.useEffect(() => {
    if (!canAccessItemImport(auth)) navigate("/items/", { replace: true });
  }, [auth, navigate]);

  const updateRows = React.useCallback((update: (current: ImportRow[]) => ImportRow[]) => {
    setRows((current) => {
      const next = update(current);
      return preview ? recalculate(next, preview) : next;
    });
  }, [preview]);

  const selectRows = (condition: ImportSelectionCondition) => {
    updateRows((current) => selectImportRows(current, condition));
  };

  const showPreview = async () => {
    if (!file) return;
    setBusy(true);
    setMessage("");
    setProgress("");
    try {
      const result = await previewItems(file);
      setPreview(result);
      setRows(recalculate(result.rows, result));
    } catch (error: any) {
      const fileError = error.response?.data?.file;
      setMessage(String((Array.isArray(fileError) ? fileError[0] : fileError)
        || error.response?.data?.message || "파일을 미리보기할 수 없습니다."));
    } finally {
      setBusy(false);
    }
  };

  const editRow = (rowNumber: number, patch: Partial<ImportRow>, resetGid = false) => {
    updateRows((current) => current.map((row) => {
      if (row.row_number !== rowNumber || row.completed) return row;
      const keyEdited = patch.shop_id !== undefined || patch.shop_item_id !== undefined || patch.compare_url !== undefined;
      const needsCheck = patch.shop_item_id !== undefined || patch.compare_url !== undefined;
      const errors = { ...row.errors };
      if (patch.shop_id) delete errors.shop_id;
      if (patch.shop_item_id !== undefined) delete errors.shop_item_id;
      if (patch.compare_url !== undefined) delete errors.compare_url;
      if (patch.compare_site !== undefined) delete errors.compare_site;
      if (patch.compare_range !== undefined) delete errors.compare_range;
      if (patch.target_rank !== undefined) {
        if (validTargetRank(patch.target_rank)) delete errors.target_rank;
        else errors.target_rank = ["목표순위는 1~3이어야 합니다."];
      }
      return {
        ...row,
        ...patch,
        errors,
        db_duplicate: keyEdited ? false : row.db_duplicate,
        shop_item_gid: resetGid ? "" : row.shop_item_gid,
        selected: patch.selected ?? false,
        needs_check: needsCheck ? true : row.needs_check,
        check_error: keyEdited || patch.shop_id !== undefined ? "" : row.check_error,
        lookup_error: resetGid ? "" : row.lookup_error,
      };
    }));
  };

  const checkEditedRow = async (rowNumber: number) => {
    const row = rows.find((item) => item.row_number === rowNumber);
    if (!row || row.completed || !row.needs_check) return;
    const errors = { ...row.errors };
    if (!row.shop_item_id.trim() || row.shop_item_id.length > 20) errors.shop_item_id = ["상품번호를 확인해 주세요."];
    else delete errors.shop_item_id;
    delete errors.compare_url;
    if (!row.shop_id || errors.shop_item_id) {
      updateRows((current) => current.map((item) => item.row_number === rowNumber ? { ...item, errors } : item));
      return;
    }
    try {
      const duplicate = await checkImportDuplicate(row);
      updateRows((current) => current.map((item) => item.row_number === rowNumber
        && item.shop_id === row.shop_id && item.shop_item_id === row.shop_item_id
        && item.compare_url === row.compare_url
        ? { ...item, errors, db_duplicate: duplicate, needs_check: false, check_error: "" }
        : item));
    } catch {
      updateRows((current) => current.map((item) => item.row_number === rowNumber
        && item.shop_id === row.shop_id && item.shop_item_id === row.shop_item_id
        && item.compare_url === row.compare_url
        ? { ...item, check_error: "중복 확인에 실패했습니다." }
        : item));
    }
  };

  const confirmSelectedCodes = async () => {
    const targets = selectedImportRows(rows);
    if (!canCheckImportSelection(rows)) return;
    const targetNumbers = new Set(targets.map((row) => row.row_number));
    setBusy(true);
    setCheckingCodes(true);
    setMessage("");
    setProgress(`상품코드 확인 0 / ${targets.length}`);
    updateRows((current) => current.map((row) => targetNumbers.has(row.row_number)
      ? { ...row, shop_item_gid: "", lookup_error: "" }
      : row));
    let success = 0;
    let failed = 0;
    try {
      await checkSelectedImportGids(targets, lookupImportGid, (row, gid, error) => {
        if (gid) success += 1;
        else failed += 1;
        updateRows((current) => current.map((item) => item.row_number === row.row_number
          ? { ...item, shop_item_gid: gid, lookup_error: gid ? "" : lookupErrorMessage(error) }
          : item));
        setProgress(`상품코드 확인 ${success + failed} / ${targets.length}`);
      });
      setMessage(`상품코드 확인 완료 ${success}건, 실패 ${failed}건`);
    } finally {
      setCheckingCodes(false);
      setBusy(false);
    }
  };

  const confirmCode = async (row: ImportRow) => {
    if (busy || !canSelectImportRow(row) || row.needs_check) return;
    setBusy(true);
    setMessage("");
    updateRows((current) => current.map((item) => item.row_number === row.row_number
      ? { ...item, shop_item_gid: "", lookup_error: "" }
      : item));
    try {
      const gid = await lookupImportGid(row);
      updateRows((current) => current.map((item) => item.row_number === row.row_number
        ? { ...item, shop_item_gid: gid, lookup_error: gid ? "" : lookupErrorMessage() }
        : item));
    } catch (error) {
      updateRows((current) => current.map((item) => item.row_number === row.row_number
        ? { ...item, shop_item_gid: "", lookup_error: lookupErrorMessage(error) }
        : item));
    } finally {
      setBusy(false);
    }
  };

  const registerRows = async () => {
    const targets = selectedImportRows(rows);
    if (!targets.length || !targets.every(canRegisterImportRow)) return;
    setBusy(true);
    setMessage("");
    let success = 0;
    let failed = 0;
    for (const [index, row] of targets.entries()) {
      setProgress(`${index + 1} / ${targets.length}`);
      try {
        await createImportRow(row);
        success += 1;
        updateRows((current) => current.map((item) => item.row_number === row.row_number
          ? { ...item, completed: true, selected: false, create_error: "" }
          : item));
      } catch (error: any) {
        failed += 1;
        const duplicate = Boolean(error.response?.data?.duplicate);
        const reason = String(error.response?.data?.message || "등록에 실패했습니다.");
        updateRows((current) => current.map((item) => item.row_number === row.row_number
          ? { ...item, db_duplicate: item.db_duplicate || duplicate, selected: false, create_error: reason }
          : item));
        if (error.response?.data?.limit_exceeded) {
          updateRows((current) => current.map((item) => ({ ...item, selected: false })));
          break;
        }
      }
    }
    setMessage(`등록 완료 ${success}건, 실패 ${failed}건`);
    setBusy(false);
  };

  if (!canAccessItemImport(auth)) return null;

  return (
    <div className="item-import-page">
      <div className="page-head d-flex justify-content-between align-items-center">
        <p className="fs-5">일괄등록</p>
        <Link to="/items/" className="btn btn-outline-secondary">상품목록으로</Link>
      </div>
      <div className="data-card p-3 mb-3">
        <div className="d-flex gap-2 align-items-center">
          <input type="file" accept=".xlsx" className="form-control" disabled={busy} onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setPreview(null);
            setRows([]);
          }} />
          <button type="button" className="btn btn-primary" disabled={!file || busy} onClick={showPreview}>미리보기</button>
        </div>
      </div>
      {message && <div className="alert alert-info" role="status">{message}</div>}
      {preview && <>
        <div className="data-card p-3 mb-3 d-flex justify-content-between align-items-center gap-3">
          <div>등록가능상품수: {preview.summary.remaining === null ? "무제한" : `${Math.max(0, preview.summary.remaining - rows.filter((row) => row.completed).length)}/${preview.summary.max_item_count}건`}, 엑셀상품건수: {rows.length}건 (중복 {rows.filter((row) => row.duplicate).length}건)
            {progress && <span className="ms-3">진행 {progress}</span>}
            {rows.some((row) => row.selected && !canRegisterImportRow(row)) && <div className="form-text">선택한 행의 상품코드와 중복 확인을 완료해 주세요.</div>}
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <span>{selectedImportRows(rows).length}건 선택</span>
            <button type="button" className="btn btn-outline-primary" disabled={busy || !canCheckImportSelection(rows)} onClick={() => void confirmSelectedCodes()}>일괄확인</button>
            <button type="button" className="btn btn-primary" disabled={busy || !canRegisterImportSelection(rows)} onClick={() => void registerRows()}>일괄등록</button>
          </div>
        </div>
        <div className="data-card item-import-table-card">
          <div className="d-flex gap-2 p-3 flex-wrap">
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => selectRows("all")}>전체 선택</button>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => selectRows("none")}>전체 해제</button>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => selectRows("unchecked")}>미확인 선택</button>
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => selectRows("ready")}>등록대기 선택</button>
          </div>
          <div
            id="item-import-gap-price-basis-help"
            className={`form-text px-3 pb-2 mt-0${showGapPriceBasisHelp ? "" : " d-none"}`}
          >
            노출가로 설정시 상호 노출가를 비교
          </div>
          <div className="table-scroll">
            <table className="table table-sm align-middle item-import-table">
            <thead><tr>
              <th>선택</th><th>순번</th><th>상태</th><th>상품명</th><th>그룹명</th><th>상점명</th><th>상점 선택</th><th>상품번호</th>
              <th aria-label="상품코드 확인"></th><th>비교사이트</th><th>가격비교 URL</th><th>내 상품 자동 지정 시도</th><th>비교범위</th>
              <th>기본가격</th><th>하한가</th><th>하한기준</th><th>순위기준</th><th>순위</th>
              <th>차액기준<button
                type="button"
                className="btn btn-link btn-sm px-1 ms-1 text-muted"
                aria-label="차액기준 설명"
                aria-expanded={showGapPriceBasisHelp}
                aria-controls="item-import-gap-price-basis-help"
                onClick={() => setShowGapPriceBasisHelp((visible) => !visible)}
              ><CircleQuestionMark size={16} aria-hidden="true" /></button></th><th>조정금액</th>
            </tr></thead>
            <tbody>{rows.map((row, index) => {
              const sequence = index + 1;
              const status = importRowStatus(row);
              const errorMessage = importRowErrorMessage(row);
              return <tr key={row.row_number} className={hasImportRegistrationError(row) ? "item-import-registration-error" : row.completed ? "opacity-50" : ""}>
              <td><input type="checkbox" aria-label={`${sequence}번 등록 선택`} checked={Boolean(row.selected)} disabled={busy || !canSelectImportRow(row)} onChange={(event) => editRow(row.row_number, { selected: event.target.checked })} /></td>
              <td>{sequence}</td><td><span className={`badge ${status.className}`} title={errorMessage || undefined} aria-label={errorMessage ? `${status.label}: ${errorMessage}` : undefined}>{status.label}{errorMessage && <span aria-hidden="true"> ⚠</span>}</span></td><td><input className="form-control form-control-sm" style={{ minWidth: 260 }} aria-label={`${sequence}번 상품명`} title={row.name} value={row.name} readOnly /></td>
              <td><input className={`form-control form-control-sm ${row.errors.group_name ? "is-invalid" : ""}`} style={{ minWidth: 160 }} aria-label={`${sequence}번 그룹명`} title={row.group_name} value={row.group_name} readOnly /></td>
              <td>{row.shop_name || "-"}</td>
              <td><select className="form-select form-select-sm" aria-label={`${sequence}번 상점 선택`} value={row.shop_id} disabled={busy || row.completed} onChange={(event) => {
                const shop = preview.shops.find((item) => item.id === event.target.value);
                editRow(row.row_number, {
                  shop_id: event.target.value,
                  expected_market_site: shop?.market_site || "",
                  rank_price_basis: "price_exposed",
                  gap_price_basis: ["auction", "gmarket", "eleventh"].includes(shop?.market_site || "") ? "price" : "price_exposed",
                }, true);
              }}>
                <option value="">-선택-</option>
                {preview.shops.map((shop) =>
                  <option key={shop.id} value={shop.id}>[{shop.market_site}] {shop.name}</option>)}
              </select></td>
              <td><input className={`form-control form-control-sm ${importCodeBorderClass(row)}`} style={{ width: 120 }} value={row.shop_item_id} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { shop_item_id: event.target.value }, true)} onBlur={() => void checkEditedRow(row.row_number)} /></td>
              <td><input type="hidden" value={row.shop_item_gid} readOnly /><button type="button" className="btn btn-outline-primary btn-sm" disabled={busy || !canSelectImportRow(row) || row.needs_check} onClick={() => void confirmCode(row)}>확인</button>{row.shop_item_gid && <small className="d-block text-muted">{row.shop_item_gid}</small>}</td>
              <td><select className={`form-select form-select-sm ${row.errors.compare_site ? "is-invalid" : ""}`} aria-label={`${sequence}번 비교사이트`} value={row.compare_site} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { compare_site: event.target.value, compare_site_fallback: false, try_assign_my_compare_item: canTryAssignMyCompareItem(event.target.value, row.compare_url) })}>
                <option value="danawa">다나와</option><option value="enuri">에누리</option><option value="naver">네이버</option>
              </select>{row.compare_site_fallback && <small>(임시)</small>}</td>
              <td><input className={`form-control form-control-sm ${row.errors.compare_url ? "is-invalid" : ""}`} style={{ minWidth: 260 }} title={row.compare_url} value={row.compare_url} disabled={busy || row.completed} onChange={(event) => {
                const inferred = inferCompareSite(event.target.value);
                editRow(row.row_number, { compare_url: event.target.value, compare_site: inferred.site, compare_site_fallback: inferred.fallback, try_assign_my_compare_item: canTryAssignMyCompareItem(inferred.site, event.target.value) });
              }} onBlur={() => void checkEditedRow(row.row_number)} /></td>
              <td><input type="checkbox" aria-label={`${sequence}번 내 상품 자동 지정 시도`} checked={Boolean(row.try_assign_my_compare_item)} disabled={busy || row.completed || !canTryAssignMyCompareItem(row.compare_site, row.compare_url)} onChange={(event) => editRow(row.row_number, { try_assign_my_compare_item: event.target.checked })} /></td>
              <td><select className="form-select form-select-sm" value={row.compare_range} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { compare_range: event.target.value as ImportRow["compare_range"] })}>
                <option value="">-선택-</option>
                <option value="items_all">전체상품</option><option value="items_same_market">동일마켓상품</option><option value="items_specified">지정상품</option>
              </select></td>
              <td>{row.price_default}</td><td>{row.adjust_value_min}</td>
              <td><select className="form-select form-select-sm" aria-label={`${sequence}번 하한기준`} defaultValue="price" disabled={busy || row.completed}>
                <option value="price">판매가</option>
              </select></td>
              <td><select className="form-select form-select-sm" aria-label={`${sequence}번 순위기준`} value={row.rank_price_basis} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { rank_price_basis: event.target.value as ImportRow["rank_price_basis"] })}>
                <option value="price_exposed">노출가</option><option value="price" disabled={!auth.subscriptionStatus?.item_is_premium}>판매가</option>
              </select></td>
              <td><select className={`form-select form-select-sm ${validTargetRank(Number(row.target_rank)) ? "" : "is-invalid"}`} aria-label={`${sequence}번 기준 순위`} value={Number.isInteger(Number(row.target_rank)) && Number(row.target_rank) >= 1 && Number(row.target_rank) <= 10 ? row.target_rank : ""} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { target_rank: Number(event.target.value) })}>
                <option value="">-선택-</option>
                {Array.from({ length: 10 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}위</option>)}
              </select></td><td><select className="form-select form-select-sm" aria-label={`${sequence}번 차액기준`} value={row.gap_price_basis} disabled={busy || row.completed} onChange={(event) => editRow(row.row_number, { gap_price_basis: event.target.value as ImportRow["gap_price_basis"] })}>
                <option value="price_exposed">노출가</option><option value="price">판매가</option>
              </select></td><td>{row.desired_gap}</td>
              </tr>;
            })}</tbody>
            </table>
          </div>
        </div>
      </>}
      <Modal show={checkingCodes} onHide={() => {}} size="sm" backdrop="static">
        <Modal.Header><Modal.Title>상품코드 일괄확인</Modal.Title></Modal.Header>
        <Modal.Body>
          <div role="status" aria-live="polite">{progress}</div>
          <div>확인이 끝날 때까지 기다려 주세요.</div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
